import React, { type ReactNode } from 'react';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import { unified } from 'unified';
import { UUID_REGEX } from '@/constants/chat';
import type { ChatMessage } from '@/types/chat';
import { sanitizeDraftMarkdown } from './draftMarkdown';

export interface MatchIndex {
  messageId: string;
  /** Offset in the message text, or the match's ordinal inside the draft for a draft match. */
  index: number;
  /** Set when the match is in a draft shown under the message. */
  draftVersionId?: string;
}

/** Minimal message shape used for search (id + content + the drafts shown under it). */
export type MessageWithContent = Pick<ChatMessage, 'id' | 'content'> & Partial<Pick<ChatMessage, 'drafts'>>;

/** `query` as a global, case-insensitive regex, or `null` when it is blank. */
export function searchRegex(query: string): RegExp | null {
  const q = query.trim();
  return q ? new RegExp(escapeRegex(q), 'gi') : null;
}

export function countMatches(text: string, query: string): number {
  const regex = searchRegex(query);
  return regex ? (text.match(regex)?.length ?? 0) : 0;
}

/**
 * Returns ordered list of match indices across messages (one per occurrence), in reading order:
 * a message's matches, then those of each draft under it. Used for "N of M matches" and
 * scrolling to the Nth match.
 */
export function getMessageMatchIndices(
  messages: ReadonlyArray<MessageWithContent>,
  query: string,
  draftMatchCounts: Readonly<Record<string, number>> = {},
): MatchIndex[] {
  const regex = searchRegex(query);
  if (!regex) return [];
  const result: MatchIndex[] = [];
  for (const msg of messages) {
    const text = msg.content ?? '';
    let match: RegExpExecArray | null;
    regex.lastIndex = 0;
    while ((match = regex.exec(text)) !== null) {
      result.push({ messageId: msg.id, index: match.index });
    }
    for (const draft of msg.drafts ?? []) {
      const count = draftMatchCounts[draft.draftVersionId] ?? 0;
      for (let index = 0; index < count; index++) {
        result.push({ messageId: msg.id, index, draftVersionId: draft.draftVersionId });
      }
    }
  }
  return result;
}

interface MarkdownNode {
  type: string;
  value?: string;
  children?: MarkdownNode[];
}

// Blocks the editor renders as their own element; their text never runs into the next block's.
const DRAFT_TEXT_BLOCKS = new Set(['paragraph', 'heading', 'tableCell', 'code']);

const markdownNodeText = (node: MarkdownNode): string => {
  if (node.type === 'text' || node.type === 'inlineCode' || node.type === 'code') {
    return (node.value ?? '').replace(/\s+/g, ' ');
  }
  const text = (node.children ?? []).map(markdownNodeText).join('');
  return DRAFT_TEXT_BLOCKS.has(node.type) ? `${text}\n` : text;
};

/**
 * The text a draft shows in the editor, one block per line, without citations. Takes the markdown
 * `/chat/draft/get-version` returns for an empty `citationIndexMap` (citations as `{{id}}`).
 */
export function draftMarkdownToSearchText(markdown: string): string {
  const withoutCitations = markdown.replace(new RegExp(UUID_REGEX.source, UUID_REGEX.flags), '');
  const tree = unified().use(remarkParse).use(remarkGfm).parse(sanitizeDraftMarkdown(withoutCitations));
  return markdownNodeText(tree as MarkdownNode);
}

const DRAFT_DOM_BLOCKS = 'p, h1, h2, h3, h4, h5, h6, li, td, th, pre';

/**
 * Ranges of every `query` match in a rendered draft, in the same order and with the same block
 * breaks as `draftMarkdownToSearchText`. Text inside `excludeSelector` (citation pills) is skipped.
 */
export function findDraftSearchRanges(root: Element, query: string, excludeSelector: string): Range[] {
  const regex = searchRegex(query);
  if (!regex) return [];

  let fullText = '';
  const nodes: { node: Text; start: number }[] = [];
  let previousBlock: Element | null = null;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node: Node | null;
  while ((node = walker.nextNode())) {
    const parent = node.parentElement;
    if (!parent || parent.closest(excludeSelector)) continue;
    const block = parent.closest(DRAFT_DOM_BLOCKS);
    if (block !== previousBlock && fullText) fullText += '\n';
    previousBlock = block;
    nodes.push({ node: node as Text, start: fullText.length });
    fullText += node.nodeValue ?? '';
  }

  // The text node holding `offset`; an end offset belongs to the node it ends.
  const locate = (offset: number, isEnd: boolean) => {
    let found = nodes[0];
    for (const entry of nodes) {
      if (isEnd ? entry.start < offset : entry.start <= offset) found = entry;
      else break;
    }
    return { node: found.node, offset: offset - found.start };
  };

  const ranges: Range[] = [];
  let match: RegExpExecArray | null;
  while ((match = regex.exec(fullText)) !== null) {
    const start = locate(match.index, false);
    const end = locate(match.index + match[0].length, true);
    const range = document.createRange();
    range.setStart(start.node, start.offset);
    range.setEnd(end.node, end.offset);
    ranges.push(range);
  }
  return ranges;
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Highlight without changing the text colour.
export const SEARCH_MATCH_MARK_CLASS = 'bg-theme-search-highlight rounded-sm text-inherit';
// The active match uses a lighter shade, so it stands out from the others.
export const SEARCH_MATCH_MARK_ACTIVE_CLASS = 'bg-theme-search-highlight-active rounded-sm text-inherit';

/** Updates active/inactive styles on rendered search marks (no React re-render). */
export function applyActiveSearchMatch(container: HTMLElement, activeIndex: number): HTMLElement | null {
  const marks = container.querySelectorAll<HTMLElement>('mark[data-search-match="true"]');
  marks.forEach((mark, index) => {
    const isActive = index === activeIndex;
    mark.className = isActive ? SEARCH_MATCH_MARK_ACTIVE_CLASS : SEARCH_MATCH_MARK_CLASS;
    mark.toggleAttribute('data-search-match-active', isActive);
  });
  return marks[activeIndex] ?? null;
}

/**
 * Wraps every occurrence of `query` (case-insensitive) in text nodes with <mark>.
 * Recurses into React children so it works after ReactMarkdown.
 */
export function highlightMatches(children: ReactNode, query: string): ReactNode {
  if (!query?.trim()) return children;
  const q = query.trim();
  const regex = new RegExp(escapeRegex(q), 'gi');

  function process(node: ReactNode): ReactNode {
    if (typeof node === 'string') {
      const parts: ReactNode[] = [];
      let lastIndex = 0;
      let match: RegExpExecArray | null;
      regex.lastIndex = 0;
      while ((match = regex.exec(node)) !== null) {
        if (match.index > lastIndex) {
          parts.push(node.slice(lastIndex, match.index));
        }
        parts.push(
          React.createElement(
            'mark',
            {
              key: match.index,
              className: SEARCH_MATCH_MARK_CLASS,
              'data-search-match': true,
            },
            match[0],
          ),
        );
        lastIndex = regex.lastIndex;
      }
      if (lastIndex < node.length) parts.push(node.slice(lastIndex));
      return parts.length <= 1 ? (parts[0] ?? '') : React.createElement(React.Fragment, null, ...parts);
    }
    if (Array.isArray(node)) {
      return node.map((child, i) => React.createElement(React.Fragment, { key: i }, process(child)));
    }
    if (node != null && typeof node === 'object' && 'props' in node) {
      const el = node as React.ReactElement<{ children?: ReactNode; 'data-search-match'?: boolean }>;
      if (el.type === 'mark' && el.props['data-search-match']) {
        return el;
      }
      return React.cloneElement(el, {}, process(el.props?.children));
    }
    return node;
  }

  return process(children);
}
